import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MustMatch } from '../_helpers/must-match.validator';
import { UserService } from '../../core/services/user.service';
import { Subscription, Observable } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { FormsService } from '../../shared/forms.service';
import { BrandingService } from '../../core/services/branding.service';
import { DateTime } from 'luxon';
import { customEmailValidator } from '../../core/utils/custom-email-validator.utils';
import { TranslateService } from '@ngx-translate/core';
import { TermsPrivacyModalComponent } from "../../shared/terms-privacy-modal/terms-privacy-modal.component";
import { MatDialog } from "@angular/material/dialog";
import { ApiErrorResponse, ApiSuccessResponse } from '../../core/models/auth.model';

@Component({
    selector: 'app-register',
    templateUrl: './register.component.html',
    styleUrls: ['./register.component.scss'],
    standalone: false
})
export class RegisterComponent implements OnInit, OnDestroy {
  registerForm: FormGroup;
  submitted = false;
  subscription: Subscription;
  maxBirthdayDate: Date;
  minBirthdayDate: Date;
  isLoginDisabled$: Observable<string | boolean | null>;

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private router: Router,
    private toastr: ToastrService,
    private formsService: FormsService,
    private brandingService: BrandingService,
    private translate: TranslateService,
    private dialog: MatDialog
  ) {
    const url = new URL(window.location.href);
    const emailFromQuery = url.searchParams.get('email');
    this.isLoginDisabled$ = brandingService.isLoginDisabled$;
      this.registerForm = this.fb.group({
        invitation_token: [null, []],
        confirm_success_url: ['confirmed', []],
        email: [emailFromQuery ?? '', [Validators.required, customEmailValidator()]],
        password: ['', [Validators.required, Validators.minLength(6)]],
        password_confirmation: ['', Validators.required],
        profile_attributes: this.fb.group({
          first_name: ['', Validators.required],
          last_name: ['', Validators.required],
          middle_name: [''],
          birth_date: ['', Validators.required],
          contact_phone: ['', Validators.required],
        }),
        terms_conditions: [false, Validators.requiredTrue]
      }, { validator: MustMatch('password', 'password_confirmation') });
  }

  ngOnInit() {
    this.brandingService.setBrandingTitle('Register');

    const today = DateTime.now();

    this.maxBirthdayDate = today.minus({ years: 18 }).toJSDate();
    this.minBirthdayDate = DateTime.fromISO('1900-01-01').toJSDate();
  }

  submitForm(): void {
    this.submitted = true;
    if (this.registerForm.invalid) {
      this.toastr.error(this.translate.instant('form.please_fill_the_form'));
      return;
    }
    const formValue = this.registerForm.value;
    const payload = {
      ...formValue,
      profile_attributes: {
        ...formValue.profile_attributes,
        birth_date: DateTime.fromJSDate(formValue.profile_attributes.birth_date).toFormat('yyyy-LL-dd')
      }
    };
    this.subscription = this.userService.register(payload)
      .subscribe(success => {
          if (success) {
            this.toastr.success('Use email and password to login.', 'Registration Success!');
            this.router.navigate(['/login']);
          }
        },
        error => this.formsService.handleFormErrors(error)
      );
  }

  onAgreementTextClick(event: MouseEvent): void {
    if (event.target instanceof HTMLElement && event.target.nodeName === "A") {
      event.preventDefault();
      event.stopPropagation();
      this.dialog.open(TermsPrivacyModalComponent, {
        maxWidth: "80vw",
        maxHeight: "80vh",
      });
    }
  }

  handleTermsClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dialog.open(TermsPrivacyModalComponent, {
      maxWidth: "80vw",
      maxHeight: "80vh",
    });
  }

  get f() {
    return this.registerForm.controls;
  }

  get profileAttributes() {
    return this.registerForm.get("profile_attributes")["controls"];
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
